/**
 * PortalModeToggle — the Einfach | Gruppenbasiert mode switch (build-spec §Web).
 * Two selectable cards, each with a plain-language explanation so a broker
 * understands the consequence of the choice.
 */

import { Eye, LayoutGrid } from 'lucide-react';
import { cn } from '../../../lib/cn';
import type { PortalVisibilityMode } from '../types';

interface Props {
  mode: PortalVisibilityMode;
  onChange: (mode: PortalVisibilityMode) => void;
  disabled?: boolean;
  pending?: boolean;
}

const OPTIONS: {
  value: PortalVisibilityMode;
  label: string;
  hint: string;
  icon: typeof Eye;
}[] = [
  {
    value: 'einfach',
    label: 'Einfach',
    hint: 'Alle Portal-Nutzer dieser Kundin / dieses Kunden sehen alles: sämtliche Policen, Dokumente, Rechnungen und Schäden.',
    icon: Eye,
  },
  {
    value: 'gruppenbasiert',
    label: 'Gruppenbasiert',
    hint: 'Sichtbarkeit nach Versicherungskategorie. So machen Sie z. B. Personenversicherungen mit Lohndaten nur für HR sichtbar.',
    icon: LayoutGrid,
  },
];

export function PortalModeToggle({ mode, onChange, disabled, pending }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">Portal-Sichtbarkeit</span>
        {pending && <span className="text-xs text-muted-foreground">Wird gespeichert …</span>}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Portal-Sichtbarkeit">
        {OPTIONS.map((opt) => {
          const active = mode === opt.value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled || pending}
              onClick={() => !active && onChange(opt.value)}
              className={cn(
                'flex flex-col gap-1.5 rounded-xl border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                active
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-card hover:border-primary/40',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Icon className="size-4 text-muted-foreground" />
                {opt.label}
              </span>
              <span className="text-xs text-muted-foreground">{opt.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
